import React from "react";

import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowRight } from "@fortawesome/sharp-regular-svg-icons/faArrowRight";
import { faArrowRightLong } from "@fortawesome/sharp-regular-svg-icons/faArrowRightLong";
import { faMessages } from "@fortawesome/sharp-regular-svg-icons/faMessages";
import { faPlayCircle } from "@fortawesome/sharp-regular-svg-icons/faPlayCircle";
import { faSquareTerminal } from "@fortawesome/sharp-regular-svg-icons/faSquareTerminal";
import clsx from "clsx";

import { variantBackgroundColor } from "~styles/common/variant.backgroundColor.css";
import { getSprinkles } from "~styles/getSprinkles.css";

import { Box } from "~components/Box";
import { ButtonLink } from "~components/ButtonLink";
import { EmbedWistia } from "~components/EmbedWistia";
import { IconFontAwesome } from "~components/IconFontAwesome";
import { Link } from "~components/Link";
import { StoryblokEditable } from "~components/StoryblokEditable";
import { playButtonStyle } from "~components/VideoPlayButton/styles.css";

import * as styles from "../styles.css";
import { CardDate } from "../subComponents/CardDate";
import { CardDescription } from "../subComponents/CardDescription";
import { CardIcon } from "../subComponents/CardIcon";
import { CardImage } from "../subComponents/CardImage";
import { CardLocation } from "../subComponents/CardLocation";
import { CardLogo } from "../subComponents/CardLogo";
import { CardLogosList } from "../subComponents/CardLogosList";
import { CardPrefix } from "../subComponents/CardPrefix";
import { CardTitle } from "../subComponents/CardTitle";
import { CardTopic } from "../subComponents/CardTopic";

import type { VariantCardProps } from "../types";
import type { LinkProps } from "~components/Link";

const ICON_MAP: Record<string, IconProp> = {
  faArrowRightLong,
  faArrowRight,
  faSquareTerminal,
  faMessages,
} as const;

export function GenericCard({
  userClassName,
  atomProps,
  otherProps,
  appearance,
  image,
  title,
  description,
  logo,
  date,
  location,
  brandIcon,
  prefix,
  logos,
  ctaText,
  ctaIcon,
  link,
  // New property to control link behavior
  linkBehavior = "card", // Default to the entire card being a link
  primaryTopic,
  video,
  outlined,
  background,
}: VariantCardProps) {
  const dynamicCTAIcon = ctaIcon && (ICON_MAP[ctaIcon] as IconProp);
  // Determine if the main card wrapper should be a link
  const isCardLink = link && linkBehavior === "card";
  // Determine if the CTA should be the link
  const isCtaLink = link && linkBehavior === "cta";

  const cardWrapperClassNames = clsx(
    userClassName,
    getSprinkles({
      ...atomProps,
    }),
    styles.getCardStyles({
      appearance,
      outlined,
      backgroundColor: background,
    }),
    background && variantBackgroundColor[background]
  );

  // The wrapper is only a link if isCardLink is true.
  // Otherwise, the Link component will use its fallbackElement ('div').
  const linkProps: LinkProps = {
    fallbackElement: "div",
    link: isCardLink ? link : undefined,
    className: cardWrapperClassNames,
    ...otherProps,
  };

  const [wistiaVideo] = video || [];

  return (
    <Link {...linkProps}>
      {/* Image */}
      {image && (
        <CardImage
          boxClassName={styles.imageOuter}
          position="relative"
          height="100%"
          width="auto"
          image={image}
          imageClassName={styles.imageInner}
          objectFit="cover"
        >
          {appearance === "video" && (
            <IconFontAwesome icon={faPlayCircle} className={playButtonStyle} />
          )}
        </CardImage>
      )}

      {/* Video */}
      {wistiaVideo && (
        <Box width="100%" objectFit="cover">
          <StoryblokEditable {...wistiaVideo}>
            <EmbedWistia {...wistiaVideo} />
          </StoryblokEditable>
        </Box>
      )}

      {/* Brand icon */}
      {brandIcon && !image && <CardIcon brandIcon={brandIcon} />}

      {
        <div className={styles.cardInner}>
          <div className={styles.cardHeader}>
            {/* Info (topic, date, location, logo) */}
            {(primaryTopic || date || location || logo) && (
              <Box
                display="flex"
                gap="spacing1"
                marginBottom="spacing1"
                justifyContent="space-between"
                alignItems="center"
              >
                {logo && <CardLogo logo={logo} />}
                {(primaryTopic || date || location) && (
                  <Box
                    display="flex"
                    flexDirection={logo ? "column" : "row"}
                    justifyContent={logo ? "center" : "start"}
                    width={logo ? "auto" : "100%"}
                    rowGap={logo ? "spacing1.5" : "none"}
                    alignItems="end"
                  >
                    {date && <CardDate date={date} />}
                    {primaryTopic && <CardTopic topic={primaryTopic} />}
                    {location && <CardLocation location={location} />}
                  </Box>
                )}
              </Box>
            )}

            {/* Prefix & title */}
            {prefix && <CardPrefix {...{ prefix }} />}

            {/* Title */}
            {title && <CardTitle title={title!} />}
          </div>

          {/* Description */}
          {description && <CardDescription text={description} />}

          {/* Logos */}
          {logos && <CardLogosList {...{ logos }} />}

          {/* Cta text */}
          {/* Pass the link to the CTA only if linkBehavior is 'cta' */}
          {ctaText && isCtaLink && (
            <ButtonLink
              appearance="secondary"
              link={link}
              marginTop="spacing1"
              iconLeft={dynamicCTAIcon as IconProp}
              shouldHaveArrowIcon={!ctaIcon}
            >
              {ctaText}
            </ButtonLink>
          )}
        </div>
      }
    </Link>
  );
}
